import { useMemo, useState } from 'react';
import { Person } from '../../../types/people';

export const usePeopleFilters = (data: Person[], searchQuery: string) => {
  const [appliedFilters, setAppliedFilters] = useState<any>({
    state: { values: [] },
    missingInfo: { values: [] },
  });

  // Extract unique states from data for filter options
  const stateOptionsMap = useMemo(() => {
    const states = new Set<string>();
    data.forEach(person => {
      if (person.state) {
        states.add(person.state);
      }
    });
    return new Map(Array.from(states).sort().map(state => [state, state]));
  }, [data]);

  const missingInfoOptionsMap = useMemo(() => (
    new Map<string, string>([
      ['email', 'Missing Email'],
      ['phone', 'Missing Phone'],
      ['address', 'Missing Address'],
      ['state', 'Missing State'],
    ])
  ), []);

  // Filter data based on search query and applied filters
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(person => 
        person.firstName.toLowerCase().includes(query) ||
        person.lastName.toLowerCase().includes(query) ||
        person.email.toLowerCase().includes(query) ||
        person.phoneNumber.toLowerCase().includes(query) ||
        person.address.toLowerCase().includes(query)
      );
    }

    // Apply state filter
    if (appliedFilters.state?.values && appliedFilters.state.values.length > 0) {
      result = result.filter(person => 
        person.state && appliedFilters.state.values.includes(person.state)
      );
    }

    // Apply last updated date range filter (removed for now - toggle doesn't support date ranges)
    // If needed, this could be implemented as a separate custom filter

    // Apply missing information filters
    if (appliedFilters.missingInfo?.values && appliedFilters.missingInfo.values.length > 0) {
      result = result.filter(person => {
        return appliedFilters.missingInfo.values.some((missingType: string) => {
          switch (missingType) {
            case 'email':
              return !person.email || person.email.trim() === '';
            case 'phone':
              return !person.phoneNumber || person.phoneNumber.trim() === '';
            case 'address':
              return !person.address || person.address.trim() === '';
            case 'state':
              return !person.state || person.state.trim() === '';
            default:
              return false;
          }
        });
      });
    }

    return result;
  }, [data, searchQuery, appliedFilters]);

  // Define filter definitions for the filter bar
  const filterDefinitions = useMemo(() => [
    {
      field: 'state',
      type: 'toggle',
      defaultLabel: 'State',
      options: stateOptionsMap,
      filterState: appliedFilters.state,
    },
    {
      field: 'missingInfo',
      type: 'toggle',
      defaultLabel: 'Missing Information',
      options: missingInfoOptionsMap,
      filterState: appliedFilters.missingInfo,
    },
  ], [stateOptionsMap, missingInfoOptionsMap, appliedFilters]);

  return {
    filteredData,
    filterDefinitions,
    setAppliedFilters,
  };
};
